/**
 * Auto-sends the `?q=` search param the home example-prompt chips navigate with.
 * @remarks
 * The home chips route to `/knowledge-graph?q=<chip text>` and this hook submits
 * that text exactly once per distinct query, then strips the param so a later
 * "new chat" cannot re-trigger it.
 *
 * SE-6900 — the guard is keyed on the QUERY VALUE, not on a one-shot boolean.
 * The original boolean's only reset path was `prefillQuery` going falsy, which
 * depends on the fire-and-forget `router.replace` landing before the next chip
 * press. When it lost that race the param moved chip A → chip B with no falsy
 * step in between and the second send was swallowed entirely: nothing left the
 * client and the screen kept showing chip A's conversation, which reads as
 * "the second chip replayed the first chip's answer".
 *
 * A ref (rather than state) keeps this free of the `react-hooks/set-state-in-effect`
 * lint — there is no state to reset when the prop flips, only a value to compare.
 */

import { Href, useRouter } from "expo-router";
import { useEffect, useRef } from "react";

/** Inputs the auto-send needs from the chat screen. */
interface UsePrefillAutoSendParams {
  /** The live `?q=` value, if any. */
  prefillQuery: string | undefined;
  /** Whether the chat is ready to dispatch (auth + org resolved, not busy). */
  canSendMessage: boolean;
  /** Submits a question to the agent. */
  sendMessage: (text: string, researchMode?: boolean) => void;
  /** Starts a fresh thread so the prefilled question opens its own conversation. */
  createNewThread: () => Promise<void>;
}

/**
 * Submits the `?q=` prefill once per distinct query value.
 * @param params - Prefill auto-send inputs
 * @param params.prefillQuery - The live `?q=` value, if any
 * @param params.canSendMessage - Whether the chat is ready to dispatch
 * @param params.sendMessage - Submits a question to the agent
 * @param params.createNewThread - Starts a fresh thread for the prefill
 */
export const usePrefillAutoSend = ({
  prefillQuery,
  canSendMessage,
  sendMessage,
  createNewThread,
}: UsePrefillAutoSendParams): void => {
  const router = useRouter();

  /** The query value already dispatched; null once the param is cleared. */
  const sentQueryRef = useRef<string | null>(null);

  useEffect(() => {
    if (!prefillQuery) {
      // The param was cleared (by the replace below, "New Chat", or a thread
      // selection). Re-arm so pressing the SAME chip again submits again.
      sentQueryRef.current = null;
      return;
    }

    if (sentQueryRef.current === prefillQuery || !canSendMessage) {
      return;
    }
    sentQueryRef.current = prefillQuery;

    // Strip ?q= FIRST, synchronously. It used to be stripped after
    // `await createNewThread()` — which awaits a storage read, leaving a window
    // in which the user could already be back on Home tapping the next chip.
    // Clearing up front closes that window and re-arms the guard immediately,
    // so even pressing the SAME chip twice submits twice (SE-6900).
    router.replace("/knowledge-graph" as Href);

    const sendPrefill = async () => {
      await createNewThread();
      sendMessage(prefillQuery, false);
    };
    void sendPrefill();
  }, [prefillQuery, canSendMessage, sendMessage, createNewThread, router]);
};
